import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Building2,
  ChevronRight,
  Layers,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import {
  createBuilding,
  createFloor,
  deleteBuilding,
  deleteFloor,
  deleteSite,
  fetchOrganizations,
  updateBuilding,
  updateFloor,
  updateSite,
} from '@/lib/api/spaces.js'
import {
  initialModelForm,
  persistEntityModel,
  pickModelAsset,
} from '@/lib/modelPresets.js'
import ModelFormSection from '@/components/common/ModelFormSection.jsx'
import RowMenu from '@/components/common/RowMenu.jsx'
import TimezoneSelect from '@/components/common/TimezoneSelect.jsx'
import OrgMark from '@/components/common/OrgMark.jsx'

export default function SiteDetailPage() {
  const { siteId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ['orgs'],
    queryFn: fetchOrganizations,
  })

  const { site, org } = useMemo(() => {
    for (const o of orgs) {
      const s = (o.sites || []).find((x) => x.id === siteId)
      if (s) return { site: s, org: o }
    }
    return { site: null, org: null }
  }, [orgs, siteId])

  const [dialog, setDialog] = useState(null)
  const invalidateOrgs = () => queryClient.invalidateQueries({ queryKey: ['orgs'] })

  const updateSiteMutation = useMutation({
    mutationFn: async ({ siteId, site: siteRow, values }) => {
      const { entityModel, ...siteFields } = values
      const updated = await updateSite(siteId, siteFields)
      await persistEntityModel('site', siteId, siteRow, entityModel)
      return updated
    },
    onSuccess: () => { invalidateOrgs(); setDialog(null) },
  })
  const deleteSiteMutation = useMutation({
    mutationFn: deleteSite,
    onSuccess: () => { invalidateOrgs(); navigate('/spaces') },
  })
  const createBuildingMutation = useMutation({
    mutationFn: async ({ siteId, values }) => {
      const { entityModel, ...fields } = values
      const building = await createBuilding(siteId, fields)
      await persistEntityModel('building', building.id, null, entityModel)
      return building
    },
    onSuccess: () => { invalidateOrgs(); setDialog(null) },
  })
  const updateBuildingMutation = useMutation({
    mutationFn: async ({ buildingId, building, values }) => {
      const { entityModel, ...fields } = values
      const updated = await updateBuilding(buildingId, fields)
      await persistEntityModel('building', buildingId, building, entityModel)
      return updated
    },
    onSuccess: () => { invalidateOrgs(); setDialog(null) },
  })
  const deleteBuildingMutation = useMutation({
    mutationFn: deleteBuilding,
    onSuccess: invalidateOrgs,
  })
  const createFloorMutation = useMutation({
    mutationFn: async ({ buildingId, values }) => {
      const { entityModel, ...fields } = values
      const floor = await createFloor(buildingId, fields)
      await persistEntityModel('floor', floor.id, null, entityModel)
      return floor
    },
    onSuccess: () => { invalidateOrgs(); setDialog(null) },
  })
  const updateFloorMutation = useMutation({
    mutationFn: async ({ floorId, floor, values }) => {
      const { entityModel, ...fields } = values
      const updated = await updateFloor(floorId, fields)
      await persistEntityModel('floor', floorId, floor, entityModel)
      return updated
    },
    onSuccess: () => { invalidateOrgs(); setDialog(null) },
  })
  const deleteFloorMutation = useMutation({
    mutationFn: deleteFloor,
    onSuccess: invalidateOrgs,
  })

  const submitting =
    updateSiteMutation.isPending ||
    createBuildingMutation.isPending ||
    updateBuildingMutation.isPending ||
    createFloorMutation.isPending ||
    updateFloorMutation.isPending

  const error =
    updateSiteMutation.error?.uiMessage ||
    createBuildingMutation.error?.uiMessage ||
    updateBuildingMutation.error?.uiMessage ||
    createFloorMutation.error?.uiMessage ||
    updateFloorMutation.error?.uiMessage ||
    null

  const deleteError =
    deleteSiteMutation.error?.uiMessage ||
    deleteBuildingMutation.error?.uiMessage ||
    deleteFloorMutation.error?.uiMessage ||
    null

  const dismissDeleteError = () => {
    deleteSiteMutation.reset()
    deleteBuildingMutation.reset()
    deleteFloorMutation.reset()
  }

  const resetMutations = () => {
    updateSiteMutation.reset()
    createBuildingMutation.reset()
    updateBuildingMutation.reset()
    createFloorMutation.reset()
    updateFloorMutation.reset()
  }

  const handleDeleteSite = () => {
    if (!site) return
    if (window.confirm(`Delete "${site.name}"? This also removes all buildings and floors inside it.`)) {
      deleteSiteMutation.mutate(site.id)
    }
  }

  const handleDeleteBuilding = (building) => {
    if (window.confirm(`Delete "${building.name}"? This also removes all floors inside it.`)) {
      deleteBuildingMutation.mutate(building.id)
    }
  }

  const handleDeleteFloor = (floor) => {
    if (window.confirm(`Delete "${floor.name}"? This removes its draft graph and related map data.`)) {
      deleteFloorMutation.mutate(floor.id)
    }
  }

  const handleDialogSubmit = (values) => {
    if (dialog.kind === 'site') {
      updateSiteMutation.mutate({ siteId: site.id, site, values })
    } else if (dialog.kind === 'building' && dialog.mode === 'create') {
      createBuildingMutation.mutate({ siteId: site.id, values })
    } else if (dialog.kind === 'building') {
      updateBuildingMutation.mutate({
        buildingId: dialog.building.id,
        building: dialog.building,
        values,
      })
    } else if (dialog.kind === 'floor' && dialog.mode === 'create') {
      createFloorMutation.mutate({ buildingId: dialog.building.id, values })
    } else if (dialog.kind === 'floor') {
      updateFloorMutation.mutate({
        floorId: dialog.floor.id,
        floor: dialog.floor,
        values,
      })
    }
  }

  if (isLoading) {
    return (
      <main className="app-content">
        <div className="muted">Loading…</div>
      </main>
    )
  }

  if (!site) {
    return (
      <main className="app-content">
        <header className="page-header">
          <div>
            <Link to="/spaces" className="row" style={{ gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              <ArrowLeft size={14} /> Back to spaces
            </Link>
            <h1 className="page-header__title" style={{ marginTop: 8 }}>Site not found</h1>
            <div className="page-header__sub">This site may have been deleted.</div>
          </div>
        </header>
      </main>
    )
  }

  return (
    <main className="app-content">
      <header className="page-header">
        <div>
          <Link to="/spaces" className="row" style={{ gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
            <ArrowLeft size={14} /> Back to spaces
          </Link>
          <h1 className="page-header__title" style={{ marginTop: 8 }}>{site.name}</h1>
          <div className="page-header__sub">
            <span>{org?.name}</span>
            {site.address && (
              <>
                {' · '}
                <span>{site.address}</span>
              </>
            )}
            {site.timezone && (
              <>
                {' · '}
                <span>{site.timezone}</span>
              </>
            )}
          </div>
        </div>
        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={() => setDialog({ kind: 'site', mode: 'edit', site })}
          >
            <Pencil size={14} /> Edit site
          </button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={handleDeleteSite}
            disabled={deleteSiteMutation.isPending}
          >
            <Trash2 size={14} /> Delete
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setDialog({ kind: 'building', mode: 'create' })}
          >
            <Plus size={14} /> Building
          </button>
        </div>
      </header>

      {deleteError && (
        <div className="auth-error" role="alert" style={{ marginBottom: 12 }}>
          <span>{deleteError}</span>
          <button type="button" className="btn btn--sm" onClick={dismissDeleteError}>
            Dismiss
          </button>
        </div>
      )}

      <section className="card card--lg">
        <div className="card__header card__header--sm">
          <div className="row">
            <OrgMark />
            <div>
              <div style={{ fontWeight: 600 }}>{site.name}</div>
              <div className="muted" style={{ fontSize: 12 }}>
                {(site.buildings || []).length} buildings · {countFloors(site)} floors
              </div>
            </div>
          </div>
          {site.address && (
            <div className="row" style={{ gap: 6 }}>
              <MapPin size={14} color="var(--text-muted)" />
              <span className="muted" style={{ fontSize: 13 }}>{site.address}</span>
            </div>
          )}
        </div>

        <div className="col gap-12">
          {(site.buildings || []).length === 0 && (
            <div className="muted" style={{ fontSize: 13, padding: '12px 4px' }}>
              No buildings yet. Add a building to start defining floors.
            </div>
          )}
          {(site.buildings || []).map((building) => (
            <div key={building.id}>
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
                <div className="row">
                  <Building2 size={14} color="var(--text-muted)" />
                  <span style={{ fontWeight: 500 }}>{building.name}</span>
                  {building.code && (
                    <span className="muted" style={{ fontSize: 12 }}>{building.code}</span>
                  )}
                </div>
                <div className="row">
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => setDialog({ kind: 'floor', mode: 'create', building })}
                  >
                    <Plus size={14} /> Floor
                  </button>
                  <RowMenu
                    label="Building actions"
                    onEdit={() => setDialog({ kind: 'building', mode: 'edit', building })}
                    onDelete={() => handleDeleteBuilding(building)}
                  />
                </div>
              </div>
              <div className="list">
                {(building.floors || []).length === 0 && (
                  <div className="muted" style={{ fontSize: 12, padding: '12px 14px' }}>
                    No floors yet.
                  </div>
                )}
                {(building.floors || []).map((floor) => (
                  <FloorRow
                    key={floor.id}
                    floor={floor}
                    onEdit={() => setDialog({ kind: 'floor', mode: 'edit', floor })}
                    onDelete={() => handleDeleteFloor(floor)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {dialog && (
        <SpaceDialog
          dialog={dialog}
          submitting={submitting}
          error={error}
          onClose={() => {
            resetMutations()
            setDialog(null)
          }}
          onSubmit={handleDialogSubmit}
        />
      )}
    </main>
  )
}

function FloorRow({ floor, onEdit, onDelete }) {
  const navigate = useNavigate()
  const status = floor.publish_status || 'draft'
  const open = () => navigate(`/spaces/floors/${floor.id}`)
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      open()
    }
  }
  return (
    <div
      className="list-item is-interactive"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={onKeyDown}
    >
      <div className="row">
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'var(--surface-sunken)',
            color: 'var(--text-secondary)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Layers size={16} />
        </span>
        <div>
          <div style={{ fontWeight: 500 }}>{floor.name}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {floor.counts?.nodes || 0} nodes · {floor.counts?.edges || 0} edges ·{' '}
            {floor.counts?.anchors || 0} anchors
          </div>
        </div>
      </div>
      <div className="row" onClick={(e) => e.stopPropagation()}>
        <span className={`badge ${statusClass(status)}`}>{statusLabel(status)}</span>
        <ChevronRight size={16} color="var(--text-muted)" />
        <RowMenu label="Floor actions" onEdit={onEdit} onDelete={onDelete} />
      </div>
    </div>
  )
}

function SpaceDialog({ dialog, submitting, error, onClose, onSubmit }) {
  const [values, setValues] = useState(() =>
    initialValues(dialog.kind, dialog.site || dialog.building || dialog.floor || {}),
  )
  const modelEntity =
    dialog.kind === 'site'
      ? dialog.site
      : dialog.kind === 'building'
      ? dialog.building
      : dialog.kind === 'floor'
      ? dialog.floor
      : null
  const hasModelPicker =
    dialog.kind === 'site' || dialog.kind === 'building' || dialog.kind === 'floor'
  const [entityModel, setEntityModel] = useState(() =>
    initialModelForm(hasModelPicker ? pickModelAsset(modelEntity) : null),
  )

  useEffect(() => {
    setValues(initialValues(dialog.kind, dialog.site || dialog.building || dialog.floor || {}))
    if (dialog.kind === 'site') {
      setEntityModel(initialModelForm(pickModelAsset(dialog.site)))
    } else if (dialog.kind === 'building') {
      setEntityModel(initialModelForm(pickModelAsset(dialog.building)))
    } else if (dialog.kind === 'floor') {
      setEntityModel(initialModelForm(pickModelAsset(dialog.floor)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dialog.* entities are new objects each fetch; ids keep the form stable while the modal is open
  }, [dialog.kind, dialog.mode, dialog.site?.id, dialog.building?.id, dialog.floor?.id])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const update = (field, value) => setValues((current) => ({ ...current, [field]: value }))

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!values.name.trim() || submitting) return
    const payload = normalizeValues(dialog.kind, values)
    if (hasModelPicker) {
      onSubmit({ ...payload, entityModel })
    } else {
      onSubmit(payload)
    }
  }

  const patchEntityModel = (patch) => {
    setEntityModel((cur) => (typeof patch === 'function' ? patch(cur) : patch))
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="space-dialog-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id="space-dialog-title" className="modal__title">{dialogTitle(dialog)}</h2>
          <button type="button" className="icon-btn icon-btn--sm" aria-label="Close" onClick={onClose}>
            <X />
          </button>
        </div>

        <form className="modal__form" onSubmit={handleSubmit}>
          <label className="auth-field">
            <span className="auth-field__label">Name</span>
            <input
              autoFocus
              required
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder={placeholderFor(dialog.kind)}
            />
          </label>

          {dialog.kind === 'site' && (
            <>
              <label className="auth-field">
                <span className="auth-field__label">Address</span>
                <input
                  value={values.address}
                  onChange={(e) => update('address', e.target.value)}
                  placeholder="10535 Hospital Way, Mather, CA"
                />
              </label>
              <label className="auth-field">
                <span className="auth-field__label">Timezone</span>
                <TimezoneSelect
                  value={values.timezone}
                  onChange={(v) => update('timezone', v)}
                />
              </label>

              <ModelFormSection
                scope="site"
                entityId={dialog.site?.id || null}
                form={entityModel}
                onChange={patchEntityModel}
              />
            </>
          )}

          {dialog.kind === 'building' && (
            <>
              <label className="auth-field">
                <span className="auth-field__label">Code</span>
                <input
                  value={values.code}
                  onChange={(e) => update('code', e.target.value)}
                  placeholder="A"
                />
              </label>
              <ModelFormSection
                scope="building"
                entityId={dialog.building?.id || null}
                form={entityModel}
                onChange={patchEntityModel}
              />
            </>
          )}

          {dialog.kind === 'floor' && (
            <>
              <label className="auth-field">
                <span className="auth-field__label">Floor number</span>
                <input
                  type="number"
                  value={values.floor_number}
                  onChange={(e) => update('floor_number', e.target.value)}
                  placeholder="auto"
                />
              </label>
              <div className="row" style={{ alignItems: 'flex-start' }}>
                <label className="auth-field" style={{ flex: 1 }}>
                  <span className="auth-field__label">Width</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={values.width}
                    onChange={(e) => update('width', e.target.value)}
                    placeholder="40"
                  />
                </label>
                <label className="auth-field" style={{ flex: 1 }}>
                  <span className="auth-field__label">Height</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={values.height}
                    onChange={(e) => update('height', e.target.value)}
                    placeholder="30"
                  />
                </label>
              </div>
              <label className="auth-field">
                <span className="auth-field__label">Elevation</span>
                <input
                  type="number"
                  step="0.1"
                  value={values.elevation}
                  onChange={(e) => update('elevation', e.target.value)}
                  placeholder="0"
                />
              </label>
              <span className="muted" style={{ fontSize: 11 }}>
                Leave a field blank to keep the existing value (or use defaults on create).
              </span>

              <ModelFormSection
                scope="floor"
                entityId={dialog.floor?.id || null}
                form={entityModel}
                onChange={patchEntityModel}
              />
            </>
          )}

          {error && (
            <div className="auth-error" role="alert">
              <span>{error}</span>
            </div>
          )}

          <div className="modal__actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting || !values.name.trim()}>
              {submitting ? (
                <><Loader2 size={14} className="spin" /> Saving...</>
              ) : dialog.mode === 'create' ? 'Create' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function initialValues(kind, source) {
  if (kind === 'site') {
    return {
      name: source.name || '',
      address: source.address || '',
      timezone: source.timezone || '',
    }
  }
  if (kind === 'building') {
    return {
      name: source.name || '',
      code: source.code || '',
    }
  }
  return {
    name: source.name || '',
    floor_number: source.floor_number ?? '',
    width: source.width ?? '',
    height: source.height ?? '',
    elevation: source.elevation ?? '',
  }
}

function normalizeValues(kind, values) {
  if (kind === 'floor') {
    const out = { name: values.name.trim() }
    for (const key of ['floor_number', 'width', 'height', 'elevation']) {
      const raw = values[key]
      if (raw === '' || raw === null || raw === undefined) continue
      const num = Number(raw)
      if (Number.isFinite(num)) out[key] = num
    }
    return out
  }
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ]),
  )
}

function dialogTitle(dialog) {
  const noun = dialog.kind[0].toUpperCase() + dialog.kind.slice(1)
  return dialog.mode === 'create' ? `New ${noun}` : `Edit ${noun}`
}

function placeholderFor(kind) {
  if (kind === 'site') return 'Sacramento VA Medical Center'
  if (kind === 'building') return 'Main Hospital'
  return 'Floor 1'
}

function statusLabel(status) {
  if (status === 'published') return 'Published'
  if (status === 'pending_review') return 'Review'
  return 'Draft'
}

function statusClass(status) {
  if (status === 'published') return 'badge--ok'
  if (status === 'pending_review') return 'badge--warn'
  return ''
}

function countFloors(site) {
  return (site.buildings || []).reduce(
    (sum, b) => sum + (b.floors || []).length,
    0,
  )
}
